'use client';

import React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { InView } from 'react-intersection-observer';
import { SessionContext } from '@benzinga/session-context';
import { ArticleData } from '@benzinga/article-manager';
import { ButtonVariant, ErrorBoundary, Spinner } from '@benzinga/core-ui';

import { NewPartialArticleLayout } from './NewPartialArticleLayout';
import { useInfiniteArticles } from '../hooks';
import { Session } from '@benzinga/session';
import { TrackingManager } from '@benzinga/tracking-manager';
import { isGlobalImpressionStored, storeGlobalImpression } from '@benzinga/content-manager';

interface ArticleInfiniteScrollProps {
  articleData: ArticleData;
  deviceType?: string | null;
  googleNewsUrlKey?: string;
  loadMoreButtonVariant?: ButtonVariant;
  showFontAwesomeIcons?: boolean;
  showWhatsAppIcon?: boolean;
  articleScrollViewMoreLink?: string;
}

interface ArticleLayoutProps {
  articleData: ArticleData;
  lastStory: boolean;
  commonProps: CommonLayoutProps;
}

interface CommonLayoutProps {
  articleScrollViewMoreLink?: string;
  campaignStrategy: 'none';
  deviceType?: string | null;
  executeImpressionEvent: boolean;
  executePageviewEvent: boolean;
  executeReplaceHistory: boolean;
  googleNewsUrlKey: string;
  loadMoreButtonVariant?: ButtonVariant;
  partialView: boolean;
  primis: boolean;
  session: Session;
  showFontAwesomeIcons?: boolean;
  showWhatsAppIcon?: boolean;
  trackMeta: boolean;
}

const INFINITE_ARTICLE_INITIAL_COUNT = 4;

const ArticleLayoutSpinner = () => (
  <div className="w-full max-w-[1080px] my-10 mx-auto">
    <Spinner />
  </div>
);

const ArticleLayout: React.FC<ArticleLayoutProps> = ({ articleData, commonProps, lastStory }) => (
  <ErrorBoundary name="partial-article-layout">
    <React.Suspense fallback={<div />}>
      <NewPartialArticleLayout articleData={articleData} lastStory={lastStory} {...commonProps} />
    </React.Suspense>
  </ErrorBoundary>
);

export const ArticleInfiniteScrollStories: React.FC<ArticleInfiniteScrollProps> = ({
  articleData,
  articleScrollViewMoreLink,
  deviceType,
  googleNewsUrlKey = 'benzinga',
  loadMoreButtonVariant,
  showFontAwesomeIcons,
  showWhatsAppIcon,
}) => {
  const session = React.useContext(SessionContext);

  const { infiniteScrollArticles, infiniteScrollArticlesIDs, loadInfiniteArticle } = useInfiniteArticles(
    articleData,
    session,
    {
      initialCount: INFINITE_ARTICLE_INITIAL_COUNT,
    },
  );

  const commonProps: CommonLayoutProps = {
    articleScrollViewMoreLink,
    campaignStrategy: 'none',
    deviceType,
    executeImpressionEvent: true,
    executePageviewEvent: false,
    executeReplaceHistory: true,
    googleNewsUrlKey,
    loadMoreButtonVariant,
    partialView: true,
    primis: false,
    session: session.getSession(),
    showFontAwesomeIcons,
    showWhatsAppIcon,
    trackMeta: true,
  };

  if (!Array.isArray(infiniteScrollArticlesIDs)) {
    return null;
  }

  if (!infiniteScrollArticlesIDs.length) {
    return <ArticleLayoutSpinner />;
  }

  const handleLoadMore = (index: number) => {
    session.getManager(TrackingManager).trackTableEvent('load_more', 'article-infinite-scroll', 'news', {});
    loadInfiniteArticle(index);
  };

  const registerImpression = (isVisible: boolean, article: ArticleData) => {
    if (isVisible && !isGlobalImpressionStored(`${article?.nodeId}`)) {
      storeGlobalImpression(`${article?.nodeId}`);
      const sponsored = article?.meta?.Flags?.ShowAdvertiserDisclosure;
      session.getManager(TrackingManager).trackCampaignEvent('view', {
        partner_id: (article?.meta?.Reach?.Disclosure?.tid || 0).toString(),
        sponsored: String(sponsored),
        unit_type: sponsored ? `sponsored-item-${article?.nodeId}` : `article-infinite-scroll-${article?.nodeId}`,
      });
    }
  };

  return (
    <div className="min-h-[300px]">
      <InfiniteScroll
        hasMore={infiniteScrollArticlesIDs.length !== infiniteScrollArticles.length}
        loader={<ArticleLayoutSpinner key="article-layout-spinner" />}
        loadMore={(index: number) => handleLoadMore(index)}
        pageStart={INFINITE_ARTICLE_INITIAL_COUNT - 1}
        threshold={700}
      >
        {infiniteScrollArticles?.map((articleData, index) => (
          <InView
            key={`${articleData.nodeId}-${index}`}
            onChange={isVisible => registerImpression(isVisible, articleData)}
            rootMargin="100px 0px"
          >
            <ArticleLayout
              articleData={articleData}
              commonProps={commonProps}
              lastStory={infiniteScrollArticlesIDs.length - 1 === index}
            />
          </InView>
        ))}
      </InfiniteScroll>
    </div>
  );
};

export default ArticleInfiniteScrollStories;
